import { create } from 'zustand';
import { StickyNote, WhiteboardState, BranchRelationship, InheritanceType } from '../types';
import { v4 as uuidv4 } from 'uuid';
import { summarizeConversation } from '../utils/summarizeApi';
import { generateTopic } from '../utils/topicApi';
import { exportTreeToJSON } from '../utils/treeExporter';
import { auth, isFirebaseInitialized } from '../config/firebase';

// Note: Firebase auth is imported at the top level to avoid initialization issues
// that can occur when using require('firebase/auth') inside dynamic imports.
// This fixes the "Firebase: No Firebase App '[DEFAULT]' has been created" error
// that was occurring when deleting notes.


// Branch creation result codes
export enum BranchCreationResult {
  SUCCESS = 'SUCCESS',
  PARENT_NOT_FOUND = 'PARENT_NOT_FOUND',
  CANNOT_BRANCH_FROM_FIRST_MESSAGE = 'CANNOT_BRANCH_FROM_FIRST_MESSAGE',
  BRANCH_POINT_OUT_OF_RANGE = 'BRANCH_POINT_OUT_OF_RANGE',
  MAX_DEPTH_EXCEEDED = 'MAX_DEPTH_EXCEEDED',
  TOO_MANY_BRANCHES = 'TOO_MANY_BRANCHES'
}

// Note dimensions constants
const NOTE_WIDTH = 256;
const NOTE_HEIGHT = 224;

// Branch limit configuration - maximum number of branches allowed per parent note
const BRANCH_LIMIT_PER_NOTE = parseInt(process.env.REACT_APP_BRANCH_LIMIT_PER_NOTE || '5', 10);

// Helper function to calculate overlap percentage between two rectangles
const calculateOverlapPercentage = (rect1: { x: number; y: number; width: number; height: number }, rect2: { x: number; y: number; width: number; height: number }) => {
  const left1 = rect1.x;
  const right1 = rect1.x + rect1.width;
  const top1 = rect1.y;
  const bottom1 = rect1.y + rect1.height;
  
  const left2 = rect2.x;
  const right2 = rect2.x + rect2.width;
  const top2 = rect2.y;
  const bottom2 = rect2.y + rect2.height;
  
  // Calculate intersection
  const left = Math.max(left1, left2);
  const right = Math.min(right1, right2);
  const top = Math.max(top1, top2);
  const bottom = Math.min(bottom1, bottom2);
  
  if (left >= right || top >= bottom) {
    return 0; // No overlap
  }
  
  const intersectionArea = (right - left) * (bottom - top);
  const area1 = rect1.width * rect1.height;
  const area2 = rect2.width * rect2.height;
  const smallerArea = Math.min(area1, area2);
  
  return (intersectionArea / smallerArea) * 100;
};

// Helper function to check if a position overlaps with existing notes
const findNonOverlappingPosition = (notes: StickyNote[], defaultX: number, defaultY: number, maxAttempts: number = 50) => {
  const OVERLAP_THRESHOLD = 50; // 50% overlap threshold
  const Y_RANGE_MIN = window.innerHeight * 0.1; // 10% of screen height
  const Y_RANGE_MAX = window.innerHeight * 0.9; // 90% of screen height
  
  // Check if default position is suitable
  const hasOverlap = notes.some(note => {
    const overlap = calculateOverlapPercentage(
      { x: defaultX, y: defaultY, width: NOTE_WIDTH, height: NOTE_HEIGHT },
      { x: note.position.x, y: note.position.y, width: note.width, height: note.height }
    );
    return overlap > OVERLAP_THRESHOLD;
  });
  
  if (!hasOverlap) {
    return { x: defaultX, y: defaultY };
  }
  
  // Try random positions
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const randomY = Y_RANGE_MIN + Math.random() * (Y_RANGE_MAX - Y_RANGE_MIN);
    
    const hasRandomOverlap = notes.some(note => {
      const overlap = calculateOverlapPercentage(
        { x: defaultX, y: randomY, width: NOTE_WIDTH, height: NOTE_HEIGHT },
        { x: note.position.x, y: note.position.y, width: note.width, height: note.height }
      );
      return overlap > OVERLAP_THRESHOLD;
    });
    
    if (!hasRandomOverlap) {
      return { x: defaultX, y: randomY };
    }
  }
  
  // Fallback to original position if no suitable position found
  return { x: defaultX, y: defaultY };
};

const initialState: WhiteboardState = {
  notes: [],
  selectedNoteIds: [],
  scale: 0.9, // 90% default zoom (equivalent to previous 75% of 1.2)
  offset: { x: 0, y: 0 },
  isSelectionMode: false,
  selectionBox: null,
  branchRelationships: [],
  canvasName: undefined,
  canvasId: undefined,
  description: undefined,
  tags: undefined,
  lastSavedStateHash: undefined,
  hasEverBeenSaved: false,
};

// Toast state
interface ToastState {
  show: boolean;
  message: string;
  type: 'success' | 'error' | 'info';
}

// Smoothly pan to a note by id with animation
function panToNote(noteId: string) {
  const state = useWhiteboardStore.getState().state;
  const note = state.notes.find(n => n.id === noteId);
  if (!note) return;
  
  const scale = state.scale;
  const centerX = note.position.x + note.width / 2;
  const centerY = note.position.y + note.height / 2;
  const targetOffsetX = window.innerWidth / 2 / scale - centerX;
  const targetOffsetY = window.innerHeight / 2 / scale - centerY;
  
  const startOffsetX = state.offset.x;
  const startOffsetY = state.offset.y;
  const deltaX = targetOffsetX - startOffsetX;
  const deltaY = targetOffsetY - startOffsetY;
  
  // Animation duration in milliseconds
  const duration = 800;
  const startTime = performance.now();
  
  function animate(currentTime: number) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    
    // Easing function for smooth deceleration
    const easeOutCubic = 1 - Math.pow(1 - progress, 3);
    
    const currentOffsetX = startOffsetX + deltaX * easeOutCubic;
    const currentOffsetY = startOffsetY + deltaY * easeOutCubic;
    
    useWhiteboardStore.setState((state) => ({
      state: {
        ...state.state,
        offset: { x: currentOffsetX, y: currentOffsetY },
      },
    }));
    
    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  }
  
  requestAnimationFrame(animate);
}

export const useWhiteboardStore = create<{
  state: WhiteboardState;
  toast: ToastState;
  addNote: (color?: string) => void;
  addBranchNote: (parentId: string, color: string, branchPoint: number, inheritanceType: InheritanceType, customTopic?: string, onBranchCreated?: (branchId: string) => void) => Promise<BranchCreationResult>;
  updateNote: (id: string, updates: Partial<StickyNote>) => void;
  deleteNote: (id: string) => void;
  deleteSelectedNotes: () => void;
  selectNote: (id: string, isMultiSelect: boolean) => void;
  deselectAll: () => void;
  setSelectionMode: (isSelectionMode: boolean) => void;
  startSelectionBox: (startX: number, startY: number) => void;
  updateSelectionBox: (endX: number, endY: number) => void;
  endSelectionBox: () => void;
  zoom: (scale: number) => void;
  pan: (offsetX: number, offsetY: number) => void;
  getBranchRelationship: (branchId: string) => BranchRelationship | undefined;
  getChildBranches: (parentId: string) => BranchRelationship[];
  setNoteStatus: (id: string, status: 'root' | 'active' | 'merged' | 'archived') => void;
  mergeBranchToParent: (branchId: string) => void;
  archiveNote: (id: string) => void;
  copyMergeMessage: (sourceNoteId: string, messageIndex: number, targetNoteId: string, messageText: string) => void;
  undoMerge: (mergeMessageIndex: number, targetNoteId: string) => void;
  showToast: (message: string, type?: 'success' | 'error' | 'info') => void;
  hideToast: () => void;
  loadTree: (newState: WhiteboardState) => void;
  setCanvasName: (name: string | undefined) => void;
  setCanvasId: (id: string | undefined) => void;
  setSaveStatus: (hash: string) => void;
  newCanvas: () => void;
}>((set, get) => ({
  state: initialState,
  toast: { show: false, message: '', type: 'success' },



  addNote: (color = '#FFEB3B') => {
    const state = get().state;
    const centerX = window.innerWidth / 2 - NOTE_WIDTH / 2;
    const centerY = window.innerHeight / 2 - NOTE_HEIGHT / 2;
    
    // Find a non-overlapping position
    const position = findNonOverlappingPosition(state.notes, centerX, centerY);
    
    const newNote: StickyNote = {
      id: uuidv4(),
      content: '',
      position: position,
      color,
      zIndex: Date.now(),
      width: NOTE_WIDTH,
      height: NOTE_HEIGHT,
      parentId: undefined,
      level: 0,
      status: 'root',
      chatHistory: [],
      needsPersonaSelection: true, // Flag to indicate persona selection is needed
    };
    
    // Add the note first
    set((state) => ({
      state: {
        ...state.state,
        notes: [...state.state.notes, newNote],
        selectedNoteIds: [newNote.id],
      },
    }));
    
    // Check if the note is not at the center and pan to it
    const isAtCenter = Math.abs(position.x - centerX) < 10 && Math.abs(position.y - centerY) < 10;
    if (!isAtCenter) {
      // Use setTimeout to ensure the note is added before panning
      setTimeout(() => {
        panToNote(newNote.id);
      }, 100);
    }
  },

  addBranchNote: async (parentId, color, branchPoint, inheritanceType, customTopic, onBranchCreated?) => {
    // Set visual feedback: parent is creating a branch
    set((state) => ({
      state: {
        ...state.state,
        notes: state.state.notes.map((note) =>
          note.id === parentId ? { ...note, isCreatingBranch: true } : note
        ),
      },
    }));

    const state = get().state;
    const parent = state.notes.find((n) => n.id === parentId);
    if (!parent) {
      // Clear the creating branch state before returning
      set((state) => ({
        state: {
          ...state.state,
          notes: state.state.notes.map((note) =>
            note.id === parentId ? { ...note, isCreatingBranch: false } : note
          ),
        },
      }));
      return BranchCreationResult.PARENT_NOT_FOUND;
    }
    
    // Validation: cannot branch from index 0 (first message)
    if (branchPoint <= 0) {
      // Clear the creating branch state before returning
      set((state) => ({
        state: {
          ...state.state,
          notes: state.state.notes.map((note) =>
            note.id === parentId ? { ...note, isCreatingBranch: false } : note
          ),
        },
      }));
      return BranchCreationResult.CANNOT_BRANCH_FROM_FIRST_MESSAGE;
    }
    
    // Validation: check if branchPoint exists in parent's chat history
    if (!parent.chatHistory || branchPoint >= parent.chatHistory.length) {
      // Clear the creating branch state before returning
      set((state) => ({
        state: {
          ...state.state,
          notes: state.state.notes.map((note) =>
            note.id === parentId ? { ...note, isCreatingBranch: false } : note
          ),
        },
      }));
      return BranchCreationResult.BRANCH_POINT_OUT_OF_RANGE;
    }
    
    const branchLevel = (parent.level ?? 0) + 1;
    if (branchLevel > 3) {
      // Clear the creating branch state before returning
      set((state) => ({
        state: {
          ...state.state,
          notes: state.state.notes.map((note) =>
            note.id === parentId ? { ...note, isCreatingBranch: false } : note
          ),
        },
      }));
      return BranchCreationResult.MAX_DEPTH_EXCEEDED; // enforce max depth
    }
    
    // Check branch limit per parent
    const existingBranches = state.branchRelationships.filter(rel => rel.parentId === parentId);
    if (existingBranches.length >= BRANCH_LIMIT_PER_NOTE) {
      // Clear the creating branch state before returning
      set((state) => ({
        state: {
          ...state.state,
          notes: state.state.notes.map((note) =>
            note.id === parentId ? { ...note, isCreatingBranch: false } : note
          ),
        },
      }));
      return BranchCreationResult.TOO_MANY_BRANCHES;
    }
    
    const newBranchId = uuidv4();
    // Calculate position: 2 note widths distance horizontally, random Y within 2 note heights
    const xOffset = NOTE_WIDTH * 2; // 2 note widths distance
    const yRange = NOTE_HEIGHT * 2; // 2 note heights range for Y position
    
    // Calculate parent center
    const parentCenterX = parent.position.x + NOTE_WIDTH / 2;
    const parentCenterY = parent.position.y + NOTE_HEIGHT / 2;
    
    // Random Y position within the range
    const randomYOffset = (Math.random() - 0.5) * yRange; // -200 to +200 pixels
    
    const newX = parentCenterX + xOffset;
    const newY = parentCenterY + randomYOffset;
    

    let initialChatHistory: Array<{ sender: 'user' | 'ai'; text: string }> = [];
    let branchTopic: string | undefined;
    
    if (!customTopic) {
      switch (inheritanceType) {
        case 'full':
          // Inherit full conversation up to branch point
          initialChatHistory = parent.chatHistory.slice(0, branchPoint + 1);
          // Generate topic from the branch point message
          try {
            const branchPointMessage = parent.chatHistory[branchPoint];
            if (branchPointMessage) {
              const topicResult = await generateTopic(branchPointMessage.text);
              branchTopic = topicResult.topic;
            }
          } catch (error) {
            console.error('Error generating topic:', error);
            branchTopic = 'New Branch';
          }
          break;
        case 'summary':
          // Call summarize endpoint to get summary, then forward to topic endpoint
          try {
            const messagesToSummarize = parent.chatHistory.slice(0, branchPoint + 1);
            const summaryResult = await summarizeConversation(messagesToSummarize);
            initialChatHistory = [
              { sender: 'ai', text: summaryResult.summary }
            ];
            
            // Forward the summary to topic endpoint to get a new topic
            if (summaryResult.summary) {
              const topicResult = await generateTopic(summaryResult.summary);
              branchTopic = topicResult.topic;
            } else {
              branchTopic = 'Summary Branch';
            }
          } catch (error) {
            console.error('Error generating summary or topic:', error);
            initialChatHistory = [
              { sender: 'ai', text: 'Error generating summary. Please try again.' }
            ];
            branchTopic = 'Summary Branch';
          }
          break;
        case 'empty':
          initialChatHistory = [];
          branchTopic = 'New Branch';
          break;
      }
    } else {
      // If customTopic is provided, use summary or full logic based on inheritanceType
      if (inheritanceType === 'summary') {
        try {
          const messagesToSummarize = parent.chatHistory.slice(0, branchPoint + 1);
          const summaryResult = await summarizeConversation(messagesToSummarize);
          initialChatHistory = [
            { sender: 'ai', text: summaryResult.summary }
          ];
          branchTopic = customTopic;
        } catch (error) {
          console.error('[BranchNote] Error in summarizeConversation for customTopic summary branch:', error);
          initialChatHistory = [
            { sender: 'ai', text: 'Error generating summary. Please try again.' }
          ];
          branchTopic = customTopic;
        }
      } else {
        initialChatHistory = parent.chatHistory.slice(0, branchPoint + 1);
        branchTopic = customTopic;
      }
    }
    

    // Inherit files from parent note
    const inheritedFiles = parent.attachedFiles ? [...parent.attachedFiles] : [];

    
    const newNote: StickyNote = {
      id: newBranchId,
      content: '',
      position: { x: newX, y: newY },
      color,
      zIndex: Date.now(),
      width: NOTE_WIDTH,
      height: NOTE_HEIGHT,
      parentId: parent.id,
      level: branchLevel,
      status: 'active',
      chatHistory: initialChatHistory,
      topic: branchTopic,
      // Note: hasUserSetTopic is intentionally not set for AI-generated topics
      isNewBranch: true, // Visual feedback: this is a new branch
      inheritedSystemPrompt: parent.systemPrompt || parent.inheritedSystemPrompt, // Inherit original system prompt from parent
      persona: parent.persona, // Inherit persona from parent
      attachedFiles: inheritedFiles, // Inherit files from parent
    };
    
    // Create branch relationship record
    const branchRelationship: BranchRelationship = {
      parentId,
      branchPoint,
      branchId: newBranchId,
      inheritanceType,
      createdAt: new Date(),
    };
    

    set((state) => {
      return {
        state: {
          ...state.state,
          notes: state.state.notes
            .map((note) =>
              note.id === parentId ? { ...note, isCreatingBranch: false } : note
            )
            .concat(newNote),
          selectedNoteIds: [newBranchId],
          branchRelationships: [...state.state.branchRelationships, branchRelationship],
        },
      };
    });
    // Smoothly pan to the new note after a short delay (to allow animation)
    setTimeout(() => panToNote(newBranchId), 400);
    
    // Call the callback if provided
    if (onBranchCreated) {
      onBranchCreated(newBranchId);
    }
    
    return BranchCreationResult.SUCCESS;
  },

  updateNote: (id, updates) => {
    // console.log('🔄 Updating note:', { id, updates });
    set((state) => ({
      state: {
        ...state.state,
        notes: state.state.notes.map((note) =>
          note.id === id ? { ...note, ...updates } : note
        ),
      },
    }));
  },

  deleteNote: (id) => {
    set((state) => {
      // Find the note being deleted to determine its type
      const noteToDelete = state.state.notes.find((note) => note.id === id);
      
      // 1. IMMEDIATE local state update
      const newState = {
        ...state.state,
        notes: state.state.notes.filter((note) => note.id !== id),
        selectedNoteIds: state.state.selectedNoteIds.filter((noteId) => noteId !== id),
        // Also remove branch relationships for this note
        branchRelationships: state.state.branchRelationships.filter(
          (rel) => rel.branchId !== id && rel.parentId !== id
        ),
      };
      
      // 2. Execute parallel async operations for data persistence
      if (noteToDelete && auth && auth.currentUser && isFirebaseInitialized()) {
        try {
          const effectiveCanvasId = state.state.canvasId || 'default';
          const currentUser = auth.currentUser;

          if (!currentUser) {
            return { state: newState };
          }
          
          // Import and execute parallel delete operations
          import('../utils/parallelSaveUtils').then(({ executeParallelDelete }) => {
            // Determine which quotas to decrement based on note type
            const quotaOperations: Array<{ type: 'decrement'; usageType: 'threadStarters' | 'totalThreads' }> = [];
            
            if (noteToDelete.parentId) {
              // This is a branch note (thread idea) - decrement total threads
              quotaOperations.push({ type: 'decrement', usageType: 'totalThreads' });
            } else {
              // This is a root note (thread starter) - decrement thread starters
              quotaOperations.push({ type: 'decrement', usageType: 'threadStarters' });
            }
            
            // Always decrement total threads (since we're removing a note)
            quotaOperations.push({ type: 'decrement', usageType: 'totalThreads' });
            
            executeParallelDelete(
              {
                userId: currentUser.uid,
                canvasId: effectiveCanvasId,
                canvasName: state.state.canvasName,
                description: state.state.description,
                tags: state.state.tags
              },
              quotaOperations
            ).catch((error: unknown) => {
              console.error('Error in parallel delete operations:', error);
            });
          }).catch((error: unknown) => {
            console.error('Failed to import parallel save utils:', error);
          });
        } catch (error: unknown) {
          console.error('Error in parallel delete logic:', error);
          // Continue with note deletion even if parallel operations fail
        }
      }
      
      // 3. Trigger quota refresh event for UI components
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('quota-updated', {
          detail: { 
            type: 'note-deleted',
            noteType: noteToDelete?.parentId ? 'branch' : 'root',
            canvasId: state.state.canvasId || 'default'
          }
        }));
      }, 100); // Small delay to ensure operations complete
      
      return { state: newState };
    });
  },

  deleteSelectedNotes: () => {
    set((state) => {
      // Get the notes being deleted to determine their types
      const notesToDelete = state.state.notes.filter(
        (note) => state.state.selectedNoteIds.includes(note.id)
      );
      
      // 1. IMMEDIATE local state update
      const newState = {
        ...state.state,
        notes: state.state.notes.filter(
          (note) => !state.state.selectedNoteIds.includes(note.id)
        ),
        selectedNoteIds: [],
      };
      
      // 2. Execute parallel async operations for data persistence
      if (notesToDelete.length > 0 && auth && auth.currentUser && isFirebaseInitialized()) {
        try {
          const effectiveCanvasId = state.state.canvasId || 'default';
          const currentUser = auth.currentUser;

          if (!currentUser) {
            return { state: newState };
          }
          
          // Import and execute parallel delete operations
          import('../utils/parallelSaveUtils').then(({ executeParallelDelete }) => {
            // Determine which quotas to decrement based on note types
            const quotaOperations: Array<{ type: 'decrement'; usageType: 'threadStarters' | 'totalThreads' }> = [];
            
            notesToDelete.forEach((note) => {
              if (note.parentId) {
                // This is a branch note (thread idea) - decrement total threads
                quotaOperations.push({ type: 'decrement', usageType: 'totalThreads' });
              } else {
                // This is a root note (thread starter) - decrement thread starters
                quotaOperations.push({ type: 'decrement', usageType: 'threadStarters' });
              }
              
              // Always decrement total threads (since we're removing a note)
              quotaOperations.push({ type: 'decrement', usageType: 'totalThreads' });
            });
            
            executeParallelDelete(
              {
                userId: currentUser.uid,
                canvasId: effectiveCanvasId,
                canvasName: state.state.canvasName,
                description: state.state.description,
                tags: state.state.tags
              },
              quotaOperations
            ).catch((error: unknown) => {
              console.error('Error in parallel delete operations:', error);
            });
          }).catch((error: unknown) => {
            console.error('Failed to import parallel save utils:', error);
          });
        } catch (error: unknown) {
          console.error('Error in parallel delete logic:', error);
          // Continue with note deletion even if parallel operations fail
        }
      }
      
      // 3. Trigger quota refresh event for UI components
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('quota-updated', {
          detail: { 
            type: 'notes-deleted',
            count: notesToDelete.length,
            canvasId: state.state.canvasId || 'default'
          }
        }));
      }, 100); // Small delay to ensure operations complete
      
      return { state: newState };
    });
  },

  selectNote: (id, isMultiSelect) => {
    set((state) => {
      const isSelected = state.state.selectedNoteIds.includes(id);
      let newSelectedNoteIds: string[] = [];
      if (isMultiSelect) {
        newSelectedNoteIds = isSelected
          ? state.state.selectedNoteIds.filter((noteId) => noteId !== id)
          : [...state.state.selectedNoteIds, id];
      } else {
        // Always select the note, don't deselect if already selected
        newSelectedNoteIds = [id];
      }
      return {
        state: {
          ...state.state,
          selectedNoteIds: newSelectedNoteIds,
        },
      };
    });
  },

  deselectAll: () => {
    set((state) => ({
      state: {
        ...state.state,
        selectedNoteIds: [],
      },
    }));
  },

  setSelectionMode: (isSelectionMode) => {
    set((state) => ({
      state: {
        ...state.state,
        isSelectionMode,
      },
    }));
  },

  startSelectionBox: (startX, startY) => {
    set((state) => ({
      state: {
        ...state.state,
        selectionBox: {
          startX,
          startY,
          endX: startX,
          endY: startY,
        },
      },
    }));
  },

  updateSelectionBox: (endX, endY) => {
    set((state) => {
      if (!state.state.selectionBox) return { state: state.state };
      return {
        state: {
          ...state.state,
          selectionBox: {
            ...state.state.selectionBox,
            endX,
            endY,
          },
        },
      };
    });
  },

  endSelectionBox: () => {
    set((state) => {
      if (!state.state.selectionBox) return { state: state.state };
      const { startX, startY, endX, endY } = state.state.selectionBox;
      const left = Math.min(startX, endX);
      const top = Math.min(startY, endY);
      const right = Math.max(startX, endX);
      const bottom = Math.max(startY, endY);
      const selectedNoteIds = state.state.notes
        .filter((note) => {
          const noteLeft = note.position.x;
          const noteTop = note.position.y;
          const noteRight = noteLeft + note.width;
          const noteBottom = noteTop + note.height;
          return (
            noteLeft < right &&
            noteRight > left &&
            noteTop < bottom &&
            noteBottom > top
          );
        })
        .map((note) => note.id);
      return {
        state: {
          ...state.state,
          selectedNoteIds,
          selectionBox: null,
        },
      };
    });
  },

  zoom: (scale) => {
    set((state) => ({
      state: {
        ...state.state,
        scale: Math.max(0.1, Math.min(5, scale)),
      },
    }));
  },

  pan: (offsetX, offsetY) => {
    set((state) => ({
      state: {
        ...state.state,
        offset: {
          x: state.state.offset.x + offsetX,
          y: state.state.offset.y + offsetY,
        },
      },
    }));
  },

  getBranchRelationship: (branchId) => {
    const state = get().state;
    return state.branchRelationships.find(rel => rel.branchId === branchId);
  },

  getChildBranches: (parentId) => {
    const state = get().state;
    return state.branchRelationships.filter(rel => rel.parentId === parentId);
  },

  // Status management functions
  setNoteStatus: (id: string, status: 'root' | 'active' | 'merged' | 'archived') => {
    set((state) => ({
      state: {
        ...state.state,
        notes: state.state.notes.map((note) =>
          note.id === id ? { ...note, status } : note
        ),
      },
    }));
  },

  mergeBranchToParent: (branchId: string) => {
    const state = get().state;
    const branch = state.notes.find(n => n.id === branchId);
    const parent = branch?.parentId ? state.notes.find(n => n.id === branch.parentId) : null;
    
    if (!branch || !parent) return;
    
    // Update branch status to merged
    set((state) => ({
      state: {
        ...state.state,
        notes: state.state.notes.map((note) =>
          note.id === branchId ? { ...note, status: 'merged' } : note
        ),
      },
    }));
  },

  archiveNote: (id: string) => {
    set((state) => ({
      state: {
        ...state.state,
        notes: state.state.notes.map((note) =>
          note.id === id ? { ...note, status: 'archived' } : note
        ),
      },
    }));
  },

  copyMergeMessage: (sourceNoteId: string, messageIndex: number, targetNoteId: string, messageText: string) => {
    const state = get().state;
    const sourceNote = state.notes.find(n => n.id === sourceNoteId);
    const targetNote = state.notes.find(n => n.id === targetNoteId);
    
    if (!sourceNote || !targetNote) return;
    
    // Add the message to the target note's chat history
    const newMessage = { sender: 'ai' as const, text: messageText };
    const updatedTargetChatHistory = [...(targetNote.chatHistory || []), newMessage];
    
    // Add copy message metadata to the target note
    const currentCopyMessages = targetNote.copyMessages || [];
    const updatedCopyMessages = [
      ...currentCopyMessages,
      {
        messageIndex: updatedTargetChatHistory.length - 1, // Index of the newly added message
        sourceNoteId: sourceNote.id,
        sourceNoteName: sourceNote.topic || 'Untitled'
      }
    ];
    

    
    set((state) => ({
      state: {
        ...state.state,
        notes: state.state.notes.map((note) => {
          if (note.id === targetNoteId) {
            return {
              ...note,
              chatHistory: updatedTargetChatHistory,
              copyMessages: updatedCopyMessages
            };
          }

          return note;
        }),
      },
    }));
  },

  undoMerge: (mergeMessageIndex: number, targetNoteId: string) => {
    const state = get().state;
    const targetNote = state.notes.find(n => n.id === targetNoteId);
    
    if (!targetNote || !targetNote.mergeMessages) return;
    
    // Find the merge info for this message index
    const mergeInfo = targetNote.mergeMessages.find(msg => msg.messageIndex === mergeMessageIndex);
    
    if (!mergeInfo) return;
    
    // 1. Remove the merge message from chat history
    const updatedChatHistory = targetNote.chatHistory?.filter((_, index) => 
      index !== mergeMessageIndex
    ) || [];
    
    // 2. Remove the merge metadata
    const updatedMergeMessages = targetNote.mergeMessages.filter(msg => 
      msg.messageIndex !== mergeMessageIndex
    );
    
    // 3. Restore the source branch status to 'active' and remove merge visual indicators
    const updatedNotes = state.notes.map(note => 
      note.id === mergeInfo.sourceBranchId 
        ? { 
            ...note, 
            status: 'active' as const,
            showConverge: false,
            showConvergeGlow: false,
            mergeTargetId: undefined
          }
        : note
    );
    
    // 4. Update the target note
    const updatedTargetNote = {
      ...targetNote,
      chatHistory: updatedChatHistory,
      mergeMessages: updatedMergeMessages
    };
    
    set((state) => ({
      state: {
        ...state.state,
        notes: updatedNotes.map(note => 
          note.id === targetNoteId ? updatedTargetNote : note
        )
      }
    }));
  },

  loadTree: (newState: WhiteboardState) => {
    // Generate hash for the loaded state to set it as saved
    const loadedStateHash = exportTreeToJSON(newState, { forComparison: true });
    
    set(() => ({
      state: {
        ...newState,
        lastSavedStateHash: loadedStateHash,
        hasEverBeenSaved: true,
      },
    }));
  },

  // Canvas name management - removed getter since we access state directly
  
  setCanvasName: (name: string | undefined) => {
    set((state) => {
      return {
        state: {
          ...state.state,
          canvasName: name,
        },
      };
    });
  },

  setCanvasId: (id: string | undefined) => {
    set((state) => ({
      state: {
        ...state.state,
        canvasId: id,
      },
    }));
  },

  setSaveStatus: (hash: string) => {
    set((state) => ({
      state: {
        ...state.state,
        lastSavedStateHash: hash,
        hasEverBeenSaved: true,
      },
    }));
  },

  newCanvas: () => {
    set((state) => ({
      ...state,
      state: {
        ...initialState,
        // Retain canvas identity when clearing
        canvasName: state.state.canvasName,
        canvasId: state.state.canvasId,
      },
    }));
  },

  showToast: (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    set((state) => ({
      ...state,
      toast: { show: true, message, type }
    }));
    
    // Auto-hide after 3 seconds
    setTimeout(() => {
      get().hideToast();
    }, 3000);
  },

  hideToast: () => {
    set((state) => ({
      ...state,
      toast: { show: false, message: '', type: 'success' }
    }));
  },
}));